import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import GenericButton, { GenericButtonTypeEnum } from '../../components/GenericButton';
import { ReactComponent as AddIcon } from '../../assets/add-icon.svg';
import { ReactComponent as ListEmptyStateIcon } from '../../assets/list-empty-state-icon.svg';
import SearchInput from '../../components/SearchInput';
import GenericMultiSelectWithSearch, { MultiSelectOption } from '../../components/GenericMultiSelectWithSearch';
import { getActiveKindergartensForAdmin, getKindergartenLocationsAndGroups } from '../../api/kindergarten.api';
import { GroupDto } from '../../common/interfaces/kindergarten.interface';
import { createNotification } from '../../utilities/helper/notification.helper';
import { MESSAGE_TYPE } from '../../common/constants/common.constants';
import { CreatePostTypeEnum } from '../../common/enums/common.enum';
import { GetPostsParams, PostDto } from '../../common/interfaces/infoboard.interface';
import { getPosts } from '../../api/infoboard.api';
import PostItem from './components/PostItem/PostItem';
import { useNavigate } from 'react-router-dom';
import { POST_CREATE } from '../../common/constants/routes.constants';
import GenericDateRangeFilter from '../../components/GenericDateRangeFilter';
import InfiniteScroll from 'react-infinite-scroll-component';

const Infoboard = () => {
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState('');
  const [selectedKindergartenIds, setSelectedKindergartenIds] = useState<string[]>([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<CreatePostTypeEnum[]>([]);
  const [posts, setPosts] = useState<PostDto[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  const [dateRange, setDateRange] = useState<{ from?: string; to?: string }>({ from: undefined, to: undefined });

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchValue);

  const createdByOptions: MultiSelectOption[] = Object.values(CreatePostTypeEnum).map(value => ({
    id: value,
    name: value
  }));
  const [kindergartensWithLocationsAndGroups, setKindergartensWithLocationsAndGroups] = useState<MultiSelectOption[]>([]);
  const [kindergartens, setKindergartens] = useState<MultiSelectOption[]>([]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchValue), 300);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    fetchPosts(1, { searchValue: debouncedSearch });
  }, [debouncedSearch]);

  useEffect(() => {
    fetchKindergartens();
  }, []);

  useEffect(() => {
    if (selectedKindergartenIds.length > 0) {
      fetchKindergartenWithLocationsAndGroups(selectedKindergartenIds);
    } else {
      setSelectedGroupIds([]);
    }
  }, [selectedKindergartenIds]);

  const fetchPosts = async (
    pageNumber = 1,
    filters?: {
      searchValue?: string;
      kindergartenIds?: string[];
      groupIds?: string[];
      createdBy?: CreatePostTypeEnum[];
      dateRange?: { from?: string; to?: string };
    }
  ) => {
    if (loading) return;
    setLoading(true);
    try {
      const params: GetPostsParams = {
        page: pageNumber,
        perPage: 10,
        name: filters?.searchValue !== undefined ? filters?.searchValue : debouncedSearch,
        kindergartenIds: filters?.kindergartenIds ?? selectedKindergartenIds,
        groupIds: filters?.groupIds ?? selectedGroupIds,
        createdBy: filters?.createdBy ?? selectedCreatedBy,
        dateFrom: filters?.dateRange ? filters?.dateRange.from : dateRange.from,
        dateTo: filters?.dateRange ? filters.dateRange.to : dateRange.to
      };
      const response = await getPosts(params);
      const newPosts = response.results || [];

      if (pageNumber === 1) {
        setPosts(newPosts);
      } else {
        setPosts(prev => [...prev, ...newPosts]);
      }

      setHasMore(newPosts.length === 10);
      setPage(pageNumber);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchKindergartens = async (search?: string) => {
    try {
      const response = await getActiveKindergartensForAdmin(search);
      setKindergartens(response.results ?? []);
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to fetch kindergartens');
    }
  };

  const fetchKindergartenWithLocationsAndGroups = async (kindergartenIds: string[], name?: string) => {
    try {
      const response = await getKindergartenLocationsAndGroups({ kindergartenIds, name });
      const mappedOptions = response.results
        ? response.results.flatMap(
            kg =>
              kg.locations?.flatMap(
                loc =>
                  loc.groups
                    ?.filter((g): g is GroupDto & { id: string } => !!g.id)
                    .map(g => ({
                      id: String(g.id!),
                      name: g.name,
                      kindergartenName: kg.name,
                      locationName: loc.name
                    })) || []
              ) || []
          )
        : [];
      setKindergartensWithLocationsAndGroups(mappedOptions);
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, 'Failed to fetch group');
    }
  };

  const handleCreateClick = () => {
    navigate(POST_CREATE);
  };

  const handleShowResults = async (filters?: {
    kindergartenIds?: string[];
    groupIds?: string[];
    createdBy?: CreatePostTypeEnum[];
    dateRange?: { from?: string; to?: string };
  }) => {
    if (filters?.kindergartenIds) setSelectedKindergartenIds(filters?.kindergartenIds);
    if (filters?.groupIds) setSelectedGroupIds(filters?.groupIds);
    if (filters?.createdBy) setSelectedCreatedBy(filters?.createdBy);
    if (filters?.dateRange) setDateRange(filters?.dateRange);
    await fetchPosts(1, filters);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: 7
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '24px' }}>
        <Typography variant="h1">Infoboard</Typography>
        <GenericButton buttonType={GenericButtonTypeEnum.Primary} startIcon={<AddIcon />} label="Create post" onClick={handleCreateClick} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'nowrap',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start'
          }}
        >
          <SearchInput setSearchValue={setSearchValue} searchTitle="Search by" />
        </Box>
        <Box
          sx={{
            flex: '0 1 auto',
            display: 'flex',
            gap: '8px',
            flexWrap: 'no wrap',
            alignItems: 'center'
          }}
        >
          <GenericMultiSelectWithSearch
            label="Kindergartens"
            options={kindergartens}
            onClearAll={() => handleShowResults({ kindergartenIds: [] })}
            onShowResults={ids => handleShowResults({ kindergartenIds: ids })}
            searchPlaceholder="Search by name"
          />
          <GenericMultiSelectWithSearch
            label="Groups"
            options={kindergartensWithLocationsAndGroups}
            onClearAll={() => handleShowResults({ groupIds: [] })}
            onShowResults={ids => handleShowResults({ groupIds: ids })}
            searchPlaceholder="Search by name"
            disabled={selectedKindergartenIds.length === 0}
            grouped={true}
          />
          <GenericMultiSelectWithSearch
            label="Created by"
            options={createdByOptions}
            onClearAll={() => handleShowResults({ createdBy: [] })}
            onShowResults={ids => handleShowResults({ createdBy: ids as CreatePostTypeEnum[] })}
            searchPlaceholder="Search by name"
          />
          <GenericDateRangeFilter
            label="Date created"
            from={dateRange.from}
            to={dateRange.to}
            onShowResults={range => handleShowResults({ dateRange: range })}
          />
        </Box>
      </Box>
      <InfiniteScroll
        dataLength={posts.length}
        next={() => fetchPosts(page + 1)}
        style={{
          marginTop: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16
        }}
        hasMore={hasMore}
        loader={<CircularProgress />}
      >
        {posts.length === 0 && !loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 10,
              gap: 2
            }}
          >
            <ListEmptyStateIcon style={{ fontSize: 40, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              No results found for selected filters
            </Typography>
          </Box>
        ) : (
          posts.map(post => <PostItem key={post.id} post={post} />)
        )}
      </InfiniteScroll>
    </Box>
  );
};

export default Infoboard;
